/**
 * Auth slice — session state (authenticated flag + decoded JWT payload).
 *
 * The JWT cookie is the source of truth: on page load the initial state is
 * derived from it, on sign-in/sign-up the token is stored in it, and logout
 * just removes it. userData is the DECODED (not verified) token payload —
 * fine for UI decisions; real authorization happens server-side.
 */
import { createSlice } from "@reduxjs/toolkit";
import Cookies from "js-cookie";
import jwtDecode from "jwt-decode";

import { TOKEN_NAME, SESSION_EXPIRED_KEY } from "../../../constants";
import {
  doSignIn,
  doSignUp,
} from "./thunk";

const parseToken = (token) => {
  if (!token) return undefined;
  let user;
  try {
    user = jwtDecode(token);
  } catch (e) {
    console.log(e);
  }
  return user;
};

/**
 * Underhallsbannerns "stang for sessionen"-flaggor ligger i localStorage.
 * De rensas vid ny inloggning sa att bannern alltid syns minst en gang per
 * session — annars kunde en anvandare som stangt den tidigare missa ett
 * pagaende underhall helt.
 */
const clearMaintenanceDismissals = () => {
  Object.keys(localStorage)
    .filter((k) => k.startsWith("maintenance_dismissed_"))
    .forEach((k) => localStorage.removeItem(k));
};

/**
 * GF-50: clear the "you were logged out" flag that request.js sets when the
 * API rejects our token. Cleared HERE rather than where it is read, so that
 * reading stays a pure operation — see the comment in Forms/Auth/SignIn.jsx.
 *
 * Signing in is the correct moment: the flag has done its job once the user has
 * read it and acted on it. Anything left over dies with the tab — note that
 * sessionStorage is copied into a tab opened from this one, so "per-tab" is not
 * the guarantee; the cookie being gone in that tab too is what makes it safe.
 */
const clearSessionExpiredFlag = () => {
  try {
    sessionStorage.removeItem(SESSION_EXPIRED_KEY);
  } catch (e) {
    // Blocked storage — nothing was ever written, so nothing to clear.
  }
};

const authSlice = createSlice({
  name: "auth",
  initialState: {
    isFetching: false,
    authenticated: !!Cookies.get(TOKEN_NAME),
    redirectUrl: null,
    userData: parseToken(Cookies.get(TOKEN_NAME)),
  },
  reducers: {
    doLogout: (state) => {
      Cookies.remove(TOKEN_NAME);
      state.authenticated = false;
    },
    setRedirectUrl: (state, action) => {
      state.redirectUrl = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(doSignIn.fulfilled, (state, action) => {
      const token = action.payload?.access_token;
      if (!!token) {
        Cookies.set(TOKEN_NAME, token);
        clearMaintenanceDismissals();
        clearSessionExpiredFlag();
        state.authenticated = true;
        state.userData = parseToken(token);
      }
    });
    builder.addCase(doSignUp.fulfilled, (state, action) => {
      const token = action.payload?.access_token;
      if (!!token) {
        Cookies.set(TOKEN_NAME, token);
        clearMaintenanceDismissals();
        clearSessionExpiredFlag();
        state.authenticated = true;
        state.userData = parseToken(token);
      }
    });
  },
});

const { actions, reducer: authReducer } = authSlice;
export const { doLogout, setRedirectUrl } = actions;
export default authReducer;
